import * as ToastPrimitive from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from "react";

import { cn } from "../lib/utils";

export const ToastProvider = ToastPrimitive.Provider;
export const ToastViewport = forwardRef<
  ElementRef<typeof ToastPrimitive.Viewport>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    className={cn(
      "fixed bottom-6 right-6 z-[100] flex max-h-screen w-[380px] flex-col gap-2 p-4 outline-none",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = "ToastViewport";

export const toastVariants = cva(
  [
    "group relative pointer-events-auto flex w-full items-start gap-3",
    "rounded-[var(--radius-md)] p-3.5",
    "bg-[var(--color-glass-surface-deep)] backdrop-blur-[var(--blur-glass)]",
    "border shadow-[var(--shadow-glass)]",
    "data-[state=open]:animate-in data-[state=closed]:animate-out",
  ].join(" "),
  {
    variants: {
      tone: {
        default: "border-[var(--color-glass-border)] text-[var(--color-text-strong)]",
        success: "border-[oklch(70%_0.10_145_/_0.5)] text-[oklch(35%_0.15_145)]",
        error:   "border-[oklch(75%_0.10_25_/_0.5)] text-[oklch(40%_0.18_25)]",
        info:    "border-[oklch(75%_0.08_250_/_0.5)] text-[oklch(38%_0.15_250)]",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface ToastProps
  extends ComponentPropsWithoutRef<typeof ToastPrimitive.Root>,
    VariantProps<typeof toastVariants> {}

export const Toast = forwardRef<ElementRef<typeof ToastPrimitive.Root>, ToastProps>(
  ({ className, tone, ...props }, ref) => (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(toastVariants({ tone, className }))}
      {...props}
    />
  ),
);
Toast.displayName = "Toast";

export const ToastTitle = forwardRef<
  ElementRef<typeof ToastPrimitive.Title>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
));
ToastTitle.displayName = "ToastTitle";

export const ToastDescription = forwardRef<
  ElementRef<typeof ToastPrimitive.Description>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn("text-xs text-[var(--color-text-soft)] mt-0.5", className)}
    {...props}
  />
));
ToastDescription.displayName = "ToastDescription";

export const ToastClose = forwardRef<
  ElementRef<typeof ToastPrimitive.Close>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    aria-label="Close"
    className={cn(
      "ml-auto grid place-items-center w-6 h-6 rounded-[var(--radius-sm)] text-[var(--color-text-soft)] hover:bg-[oklch(0%_0_0_/_0.05)]",
      className,
    )}
    {...props}
  >
    <X size={14} />
  </ToastPrimitive.Close>
));
ToastClose.displayName = "ToastClose";
